import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Shield, CheckCircle, XCircle, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const QRVerifyPage = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (token) {
      verifyToken();
    } else {
      setLoading(false);
    }
  }, [token]);

  const verifyToken = async () => {
    try {
      const response = await axios.get(`${API_URL}/verify/${token}`);
      setData(response.data);
    } catch (error) {
      setData({ valid: false, message: 'Ralat semasa pengesahan' });
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#F9F7F5] to-[#F2EBE3]">
        <div className="animate-pulse text-[#D71920] text-lg font-medium">Mengesahkan...</div>
      </div>
    );
  }

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#F9F7F5] to-[#F2EBE3] p-4">
        <div className="text-center">
          <AlertCircle className="w-16 h-16 text-[#B45309] mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-[#1A1A1A]" >
            Token Tidak Sah
          </h1>
          <p className="text-[#555] mt-2">Sila imbas kod QR yang betul</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#F9F7F5] to-[#F2EBE3] p-4">
      <div className="w-full max-w-2xl">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-[#D71920] mb-4">
            <Shield className="w-10 h-10 text-[#D4AF37]" />
          </div>
          <h1 className="text-4xl font-bold text-[#D71920] mb-2" >
            Pengesahan Keahlian RSS
          </h1>
          <p className="text-[#555]">Rumpun Silat Sarawak</p>
        </div>

        {/* Certificate Card */}
        <div className="certificate-border bg-white rounded-lg p-8 md:p-12 relative" data-testid="verification-card">
          {/* Gold corner ornaments */}
          <div className="absolute top-0 left-0 w-16 h-16 border-t-4 border-l-4 border-[#D4AF37] rounded-tl-lg"></div>
          <div className="absolute top-0 right-0 w-16 h-16 border-t-4 border-r-4 border-[#D4AF37] rounded-tr-lg"></div>
          <div className="absolute bottom-0 left-0 w-16 h-16 border-b-4 border-l-4 border-[#D4AF37] rounded-bl-lg"></div>
          <div className="absolute bottom-0 right-0 w-16 h-16 border-b-4 border-r-4 border-[#D4AF37] rounded-br-lg"></div>

          <div className="text-center space-y-6">
            {data.valid ? (
              <>
                <div className="inline-flex items-center justify-center w-24 h-24 rounded-full bg-green-100 mb-4">
                  <CheckCircle className="w-16 h-16 text-green-600" data-testid="valid-icon" />
                </div>
                <div>
                  <h2 className="text-3xl font-bold text-[#D71920] mb-2" >
                    KEAHLIAN SAH
                  </h2>
                  <div className="gold-divider my-6"></div>
                  <div className="space-y-4 text-left max-w-md mx-auto">
                    <div className="flex justify-between items-center py-3 border-b border-[#E5E5E5]">
                      <span className="text-[#777] font-medium">Nama Ahli</span>
                      <span className="font-semibold text-[#1A1A1A]" data-testid="member-name">{data.member_name}</span>
                    </div>
                    <div className="flex justify-between items-center py-3 border-b border-[#E5E5E5]">
                      <span className="text-[#777] font-medium">Perguruan</span>
                      <span className="font-semibold text-[#1A1A1A]" data-testid="school-name">{data.school_name}</span>
                    </div>
                    <div className="flex justify-between items-center py-3">
                      <span className="text-[#777] font-medium">Status</span>
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800 border border-green-200">
                        {data.status}
                      </span>
                    </div>
                  </div>
                </div>
              </>
            ) : (
              <>
                <div className="inline-flex items-center justify-center w-24 h-24 rounded-full bg-red-100 mb-4">
                  <XCircle className="w-16 h-16 text-red-600" data-testid="invalid-icon" />
                </div>
                <div>
                  <h2 className="text-3xl font-bold text-red-600 mb-4" >
                    {data.message || 'TIDAK SAH'}
                  </h2>
                  {data.member_name && (
                    <>
                      <div className="gold-divider my-6"></div>
                      <div className="space-y-3 text-left max-w-md mx-auto">
                        <div className="flex justify-between items-center py-3 border-b border-[#E5E5E5]">
                          <span className="text-[#777] font-medium">Nama Ahli</span>
                          <span className="font-semibold text-[#1A1A1A]">{data.member_name}</span>
                        </div>
                        <div className="flex justify-between items-center py-3 border-b border-[#E5E5E5]">
                          <span className="text-[#777] font-medium">Perguruan</span>
                          <span className="font-semibold text-[#1A1A1A]">{data.school_name}</span>
                        </div>
                        <div className="flex justify-between items-center py-3">
                          <span className="text-[#777] font-medium">Status</span>
                          <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-red-100 text-red-800 border border-red-200">
                            {data.status}
                          </span>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </>
            )}

            <div className="mt-8 pt-6 border-t border-[#E5E5E5]">
              <p className="text-sm text-[#777]">
                Sistem ini diuruskan oleh Rumpun Silat Sarawak (RSS)
              </p>
            </div>
          </div>
        </div>

        <div className="text-center mt-6">
          <Button
            onClick={() => window.location.reload()}
            variant="outline"
            className="border-[#7B1113] text-[#D71920] hover:bg-[#D71920] hover:text-white"
            data-testid="scan-again-button"
          >
            Imbas Semula
          </Button>
        </div>
      </div>
    </div>
  );
};

export default QRVerifyPage;
